import axios from 'axios';
import { from, Observable } from 'rxjs';

// ─── Order Documents ──────────────────────────────────────────────────────────

/**
 * Register a document on an order and receive a presigned S3 upload URL.
 * @param {string} salesOrderId - The salesOrderGuid from the order entity
 * @param {{ Type: string, FileName: string, Format: string }} payload
 * @returns {Observable<{ document_id: string, upload: { url: string, fields: Record<string, string> } }>}
 */
export const registerOrderDocument = (salesOrderId: string, payload: Record<string, unknown>) => {
  const request = axios.post(`/orders/${salesOrderId}/documents`, payload).then((response) => response.data ?? null);
  return from(request);
};

/**
 * Fetch all documents for an order (used for polling status).
 * @param {string} salesOrderId
 * @returns {Observable<Array<{ DocumentId: string, Type: string, FileName: string, Status: string, Error: string | null }>>}
 */
export const getOrderDocuments = (salesOrderId: string) => {
  const request = axios.get(`/orders/${salesOrderId}/documents`).then((response) => response.data ?? []);
  return from(request);
};

/**
 * Delete a document from an order.
 * Returns 204 on success, 409 if linked shipment document is in queued/sent status.
 * @param {string} salesOrderId
 * @param {string} documentId
 * @returns {Observable}
 */
export const deleteOrderDocument = (salesOrderId: string, documentId: string) => {
  const request = axios.delete(`/orders/${salesOrderId}/documents/${documentId}`).then((response) => response.data ?? null);
  return from(request);
};

// ─── Shipment Documents ───────────────────────────────────────────────────────

/**
 * Register a document on a shipment and receive a presigned S3 upload URL.
 * Requires CarrierCode + TrackingNumber in addition to document metadata.
 * @param {string} shipmentId
 * @param {{ type: string, fileName: string, format: string, carrierCode: string, trackingNumber: string }} payload
 * @returns {Observable<{ document_id: string, upload: { url: string, fields: Record<string, string> } }>}
 */
export const registerShipmentDocument = (shipmentId: string, payload: Record<string, unknown>) => {
  const request = axios.post(`/shipments/${shipmentId}/documents`, payload).then((response) => response.data ?? null);
  return from(request);
};

/**
 * Fetch all documents for a shipment (used for polling status).
 * Statuses: awaiting_upload → processing → processing_claimed → uploaded → queued → sent | failed
 * @param {string} shipmentId
 * @returns {Observable<Array<{ DocumentId: string, Type: string, FileName: string, Status: string, Error: string | null, ExternalId: string | null }>>}
 */
export const getShipmentDocuments = (shipmentId: string) => {
  const request = axios.get(`/shipments/${shipmentId}/documents`).then((response) => response.data ?? []);
  return from(request);
};

/**
 * Delete a document from a shipment.
 * Returns 204 on success, 409 if document is in queued/sent status.
 * Documents in failed status CAN be deleted.
 * @param {string} shipmentId
 * @param {string} documentId
 * @returns {Observable}
 */
export const deleteShipmentDocument = (shipmentId: string, documentId: string) => {
  const request = axios.delete(`/shipments/${shipmentId}/documents/${documentId}`).then((response) => response.data ?? null);
  return from(request);
};

/**
 * Send all uploaded documents for a shipment to the carrier.
 * Idempotent — backend skips documents that have already been sent.
 * @param {string} shipmentId
 * @returns {Observable<{ sent_count: number, document_ids: string[] }>}
 */
export const sendShipmentDocuments = (shipmentId: string) => {
  const request = axios.post(`/shipments/${shipmentId}/documents/send`).then((response) => response.data ?? null);
  return from(request);
};

// ─── File Upload ─────────────────────────────────────────────────────────────

/**
 * Upload a file to S3 via presigned POST (2nd step of the 2-step upload flow).
 *
 * The register endpoint returns a presigned POST payload containing a URL and
 * form fields (policy, signature, etc.). We build a multipart form with those
 * fields first, then append the file as the last field.
 *
 * Uses native fetch (not the app's axios instance) because the request goes
 * directly to S3 — no ShipStation auth headers, and the browser must set
 * the multipart Content-Type boundary automatically.
 *
 * CORS handling: In production, S3 returns proper CORS headers and we can read
 * the response status (204 = success, 403 = policy rejection). In local dev the
 * bucket may not whitelist localhost, so the browser blocks the response with a
 * TypeError even though S3 accepted the file. We catch TypeError and treat it
 * as success so the flow works in both environments.
 * @param {{ url: string, fields: Record<string, string> }} upload - Presigned POST payload from register
 * @param {File} file
 * @returns {Observable}
 */
export const uploadFileToS3 = (upload: { url: string; fields: Record<string, string> }, file: File) => {
  const form = new FormData();
  Object.entries(upload.fields).forEach(([key, value]) => {
    form.append(key, value);
  });
  form.append('file', file); // MUST be the last field

  const isLocalDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

  return new Observable((observer) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) {
        const percentageComplete = Math.round((event.loaded / event.total) * 100);
        observer.next({ type: 'progress', percentageComplete });
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status === 204) {
        observer.next(null);
        observer.complete();
      } else if (xhr.status === 403) {
        observer.error(new Error('Upload rejected — file size or type exceeded policy limits'));
      } else {
        observer.error(new Error(`Upload failed: ${xhr.status}`));
      }
    });

    xhr.addEventListener('error', () => {
      // In local dev, S3 may not have CORS configured for localhost — the browser
      // blocks the response even though S3 accepted the file.
      // Treat as success only in dev. In production, propagate all errors.
      if (isLocalDev) {
        observer.next(null);
        observer.complete();
      } else {
        observer.error(new Error('Upload failed: network error'));
      }
    });

    xhr.open('POST', upload.url);
    xhr.send(form);

    return () => xhr.abort();
  });
};

// ─── Carrier Capabilities ─────────────────────────────────────────────────────

/**
 * Fetch carrier document capabilities:
 * which carriers support document upload, accepted document types and file formats.
 * @returns {Observable<{ carriers: { carrier_code: string, name: string }[], document_types: string[], file_types: string[] }>}
 */
export const getCarrierDocumentCapabilities = () => {
  const request = axios.get('/carrier/document').then((response) => response.data ?? null);
  return from(request);
};

// ─── Document Download / Preview ─────────────────────────────────────────────

/**
 * Download or preview a document by its ID.
 * Response Content-Type is set to the file MIME type, Content-Disposition triggers download.
 * @param {string} documentId
 * @returns {Observable<Blob>}
 */
export const downloadDocument = (documentId: string) => {
  const request = axios.get(`/documents/${documentId}/download`, { responseType: 'blob' }).then((response) => response.data ?? null);
  return from(request);
};
